 
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractFormDialog, AbstractFormDialogData, AbstractDialogResultData } from '@next/core-lib/dialog';

import { CropAid } from 'src/app/overview/models';

import { OrderLine, OrderLineStatus, Package, SalesOrderPeriod } from '../../models';

export interface MoveProductsDialogData extends AbstractFormDialogData {
  fromPeriodId: number;
  salesOrderPeriods: SalesOrderPeriod[];
  orderLines: OrderLine[];
  cropAids: CropAid[];
  packages: Package[];
}

export interface MoveProductsDialogResult {
  selectedOrderLines: OrderLine[];
  toPeriod: SalesOrderPeriod;
}

interface ProductToMove {
  cropAidId: number;
  name: string;
  packages: PackageToMove[];
  selected: boolean;
}

interface PackageToMove {
  packageId: number;
  packageName: string;
  selected: boolean;
}

@Component({
  selector: 'app-move-products-dialog-dialog',
  templateUrl: './move-products-dialog.component.html',
  styleUrls: ['./move-products-dialog.component.scss'],
  standalone: false,
})
export class MoveProductsDialogComponent extends AbstractFormDialog<MoveProductsDialogResult> implements OnInit {
  data = inject<MoveProductsDialogData>(MAT_DIALOG_DATA);
  private readonly formBuilder = inject(FormBuilder);
  protected readonly mdDialogRef = inject<MatDialogRef<MoveProductsDialogComponent, AbstractDialogResultData<MoveProductsDialogResult>>>(MatDialogRef);

  formGroup: FormGroup = this.formBuilder.group({
    selectedProducts: this.formBuilder.array([]),
    toPeriod: ['', Validators.required],
    fromPeriod: [''],
    allSelected: [''],
  });
  products: ProductToMove[] = [];
  selectableToPeriods: SalesOrderPeriod[];
  fromOrderLines: OrderLine[];
  fromPeriod: SalesOrderPeriod;
  allSelected = true;

  ngOnInit(): void {
    this.setAvailableProducts();
    this.selectAll(true);

    this.fromPeriod = this.data.salesOrderPeriods.find((so) => so.id === this.data.fromPeriodId);
    this.selectableToPeriods = this.data.salesOrderPeriods.filter((so) => new Date(so.orderReceivedBefore) > new Date() && so.id !== this.data.fromPeriodId);

    this.formGroup.get('fromPeriod').setValue(this.fromPeriod.id);
  }

  updateAllSelected(product: ProductToMove, updatePackages = true) {
    this.allSelected = this.products.every((p) => p.selected);

    if (updatePackages && product.packages.length > 1) {
      for (const p of product.packages) {
        p.selected = product.selected;
      }
    }
  }

  someComplete() {
    return (
      !this.products.every((p) => p.selected) &&
      (this.products.some((p) => p.selected) || this.products.filter((p) => p.packages.length > 1).some((p) => p.packages.some((pa) => pa.selected)))
    );
  }

  somePackagesComplete(product: ProductToMove) {
    if (product.packages.length === 1 || product.packages.every((pa) => pa.selected)) {
      return false;
    }
    return product.packages.some((pa) => pa.selected);
  }

  updatePackageSelected(product: ProductToMove) {
    product.selected = product.packages.every((pa) => pa.selected);
    this.updateAllSelected(product, false);
  }

  selectAll(checked: boolean) {
    if (!this.products) {
      return;
    }
    for (const product of this.products) {
      product.selected = checked;
      this.updateAllSelected(product);
    }
  }

  process(_: MoveProductsDialogResult) {
    if (!this.allSelected && !this.someComplete()) {
      return;
    }

    const orderLines: OrderLine[] = [];

    for (const product of this.products) {
      if (product.packages.length === 1 && product.selected) {
        orderLines.push(...this.fromOrderLines.filter((line) => line.cropAidId === product.cropAidId));
      } else {
        for (const pack of product.packages) {
          if (pack.selected) {
            orderLines.push(...this.fromOrderLines.filter((line) => line.cropAidId === product.cropAidId && line.packageId === pack.packageId));
          }
        }
      }
    }

    super.confirm({
      selectedOrderLines: orderLines,
      toPeriod: this.selectableToPeriods.find((period) => period.id === this.formGroup.get('toPeriod').value),
    });
  }

  setAvailableProducts() {
    this.fromOrderLines = this.data.orderLines.filter(
      (ol) => ol.periodId === this.data.fromPeriodId && ol.count && ol.count > 0 && ol.status === OrderLineStatus.open,
    );

    this.products = [];

    for (const orderLine of this.fromOrderLines) {
      if (this.products.some((p) => p.cropAidId === orderLine.cropAidId)) {
        continue;
      }

      const cropAid = this.data.cropAids.find((c) => c.id === orderLine.cropAidId);

      if (!cropAid?.isActive) {
        continue;
      }

      const orderLinesWithSameCropAidId = this.fromOrderLines.filter((fo) => fo.cropAidId === orderLine.cropAidId && this.packageIsActive(fo));

      if (orderLinesWithSameCropAidId.length === 0) {
        continue;
      }

      this.products.push({
        cropAidId: orderLine.cropAidId,
        name: this.data.cropAids.find((c) => c.id === orderLine.cropAidId)?.name ?? '',
        selected: false,
        packages: orderLinesWithSameCropAidId.map((ol) => ({
          packageId: ol.packageId,
          packageName: this.data.packages.find((p) => p.id === ol.packageId)?.name ?? '',
          selected: false,
        })),
      });

      this.products.sort((a, b) => a.name.localeCompare(b.name));
    }
  }

  private packageIsActive(orderLine: OrderLine): boolean {
    const pack = this.data.packages.find((p) => p.id === orderLine.packageId);
    return pack?.isActive;
  }
}
